import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { PendingReview } from "@/hooks/usePendingReviews";

interface PendingReviewEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  review: PendingReview;
  onSave: (rawData: Record<string, unknown>) => void;
}

export const PendingReviewEditDialog = ({
  open,
  onOpenChange,
  review,
  onSave,
}: PendingReviewEditDialogProps) => {
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (review.raw_data) {
      setFormData(review.raw_data as Record<string, unknown>);
    }
  }, [review]);

  const updateField = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const updateImageUrl = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      image_url: value,
      image_credit: "",
      image_source_url: "",
    }));
  };

  const materialFields = [
    { key: "name", label: "Name (EN)", type: "text" },
    { key: "name_cn", label: "Name (CN)", type: "text" },
    { key: "family", label: "Family", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "source_paper", label: "Paper Citation", type: "text" },
    { key: "source_doi", label: "DOI", type: "text" },
    { key: "source_url", label: "Source URL", type: "text" },
    { key: "paper_abstract", label: "Abstract", type: "textarea" },
    { key: "degradation_value", label: "Degradation Value", type: "text" },
    { key: "degradation_source", label: "Degradation Source", type: "text" },
    { key: "tensile_strength_mpa", label: "Tensile Strength (MPa)", type: "number" },
    { key: "tensile_source", label: "Tensile Source", type: "text" },
    { key: "electrical_performance", label: "Electrical Performance", type: "text" },
    { key: "electrical_source", label: "Electrical Source", type: "text" },
    { key: "water_resistance", label: "Water Resistance", type: "text" },
    { key: "water_resistance_source", label: "Water Resistance Source", type: "text" },
    { key: "cost_per_kg", label: "Cost per kg", type: "number" },
    { key: "cost_source", label: "Cost Source", type: "text" },
    { key: "fabrication_method", label: "Fabrication Method", type: "text" },
    { key: "fabrication_source", label: "Fabrication Source", type: "text" },
    { key: "image_url", label: "Image URL", type: "text" },
    { key: "image_credit", label: "Image Credit", type: "text" },
    { key: "image_source_url", label: "Image Source URL", type: "text" },
  ];

  const applicationFields = [
    { key: "title", label: "Title", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "text" },
    { key: "application_type", label: "Application Type", type: "text" },
    { key: "image_url", label: "Image URL", type: "text" },
    { key: "image_credit", label: "Image Credit", type: "text" },
    { key: "image_source_url", label: "Image Source URL", type: "text" },
    { key: "source_url", label: "Source URL", type: "text" },
  ];

  const fields = review.item_type === "material" ? materialFields : applicationFields;
  const title = review.item_type === "material" 
    ? (formData.name as string) || "Edit Material"
    : (formData.title as string) || "Edit Application";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-display">Edit: {title}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4">
            {fields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key} className="font-mono text-xs uppercase">
                  {field.label}
                </Label>
                {field.type === "textarea" ? (
                  <Textarea
                    id={field.key}
                    value={(formData[field.key] as string) || ""}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    rows={3}
                  />
                ) : field.type === "number" ? (
                  <Input
                    id={field.key}
                    type="number"
                    value={(formData[field.key] as number) || ""}
                    onChange={(e) => updateField(field.key, e.target.value ? Number(e.target.value) : null)}
                  />
                ) : (
                  <Input
                    id={field.key}
                    value={(formData[field.key] as string) || ""}
                    onChange={(e) => field.key === "image_url"
                      ? updateImageUrl(e.target.value)
                      : updateField(field.key, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => onSave(formData)}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
